declare global {
  interface Window {
    __h2okayBusy?: boolean;
  }
}

const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

/** Watch the deploy manifest and defer refresh until a safe navigation. */
export function createVersionCheck(
  build = process.env.REACT_APP_BUILD_ID,
  reload = () => window.location.reload(),
) {
  let changed = false;
  let reloading = false;
  let stopped = false;
  let hiddenSince: number | undefined = document.hidden ? Date.now() : undefined;
  let timer: ReturnType<typeof setInterval> | undefined;

  /** Refresh only when the user has no unsynced work. */
  const routeChanged = () => {
    if (changed && !stopped && !reloading && !window.__h2okayBusy &&
        !document.querySelector('form [data-dirty="true"]')) {
      reloading = true;
      reload();
    }
  };

  /** Ignore offline, failed, and malformed manifests. */
  const check = async () => {
    if (!build || stopped) return;
    try {
      const response = await fetch('/version.json?ts=' + Date.now(), { cache: 'no-store' });
      if (!response.ok) return;
      const version = await response.json();
      if (!stopped && typeof version.build === 'string' && version.build) {
        changed = version.build !== build;
      }
    } catch {
      // Offline tabs keep working until a later check succeeds.
    }
  };

  /** Record hiding without losing the duration before the focus event. */
  const visibilityChanged = () => {
    if (document.hidden && hiddenSince === undefined) hiddenSince = Date.now();
  };

  /** Recheck on focus and refresh tabs returning from a long absence. */
  const focused = async () => {
    const longHidden = hiddenSince !== undefined && Date.now() - hiddenSince >= THIRTY_MINUTES;
    hiddenSince = undefined;
    await check();
    if (longHidden) routeChanged();
  };

  /** Start polling and browser listeners; return their cleanup function. */
  const start = () => {
    stopped = false;
    void check();
    timer = setInterval(check, FIVE_MINUTES);
    document.addEventListener('visibilitychange', visibilityChanged);
    window.addEventListener('focus', focused);
    return stop;
  };

  /** Remove timers and listeners and ignore pending fetches. */
  const stop = () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', visibilityChanged);
    window.removeEventListener('focus', focused);
  };

  return { start, stop, check, routeChanged };
}
